import { computed, inject, Injectable, signal } from '@angular/core';
import { StorageService } from '@core/services/storage.service';
import { UserService } from '@core/services/user.service';
import {
  ResultadoBusqueda,
  TIPOS_RESULTADO,
  TipoResultado,
} from '@shared/models/busqueda-global.model';

const LS_OPERACIONES = 'lrp-prov-busqueda-recientes';
const LS_CONSULTAS = 'lrp-prov-busqueda-consultas';

const MAX_OPERACIONES = 8;
const MAX_CONSULTAS = 6;

/**
 * Una operación abierta desde el buscador. Se guarda lo justo para volver a
 * pintar la fila sin llamar al backend, más el alcance (`empresaId`,
 * `proveedorId`) con el que se abrió para poder filtrarla por el alcance activo.
 */
export interface OperacionReciente {
  tipo: TipoResultado;
  id: number;
  numero: string;
  empresaId: number;
  proveedorId: number;
  empresa: string | null;
  abierto: boolean;
  ts: number;
}

/**
 * Historial del buscador global, persistido en localStorage.
 *
 * Dos listas distintas a propósito:
 *   - RECIENTES: operaciones que el usuario abrió (un clic vuelve a abrirlas).
 *   - BÚSQUEDAS RECIENTES: los textos escritos (un clic repite la consulta).
 */
@Injectable({ providedIn: 'root' })
export class BusquedaRecientesService {
  private readonly storage = inject(StorageService);
  private readonly userService = inject(UserService);

  private readonly operacionesRaw = signal<OperacionReciente[]>(
    this.leerOperaciones(),
  );
  private readonly consultasRaw = signal<string[]>(this.leerConsultas());

  /**
   * Operaciones del alcance activo (empresa + proveedor). Se filtra en LECTURA y
   * no al guardar: la lista sobrevive a los cambios de empresa, así que al volver
   * a la empresa anterior el historial sigue ahí en vez de haberse vaciado.
   */
  readonly operaciones = computed<OperacionReciente[]>(() => {
    const empresaActiva = this.userService.companyId();
    const proveedorActivo = this.userService.proveedorId();
    if (empresaActiva === null || proveedorActivo === null) return [];

    return this.operacionesRaw().filter(
      (o) => o.empresaId === empresaActiva && o.proveedorId === proveedorActivo,
    );
  });

  readonly consultas = computed<string[]>(() => this.consultasRaw());

  readonly hayHistorial = computed(
    () => this.operaciones().length > 0 || this.consultas().length > 0,
  );

  /** Registra una operación abierta. Si ya estaba, sube al principio. */
  registrarOperacion(r: ResultadoBusqueda): void {
    const empresaId = this.userService.companyId();
    const proveedorId = this.userService.proveedorId();
    if (empresaId === null || proveedorId === null) return;

    const entrada: OperacionReciente = {
      tipo: r.tipo,
      id: r.id,
      numero: r.numero,
      empresaId,
      proveedorId,
      empresa: r.empresa,
      abierto: r.abierto,
      ts: Date.now(),
    };
    const resto = this.operacionesRaw().filter(
      (o) =>
        !(
          o.tipo === entrada.tipo &&
          o.id === entrada.id &&
          o.empresaId === empresaId &&
          o.proveedorId === proveedorId
        ),
    );
    const lista = [entrada, ...resto].slice(0, MAX_OPERACIONES);
    this.operacionesRaw.set(lista);
    this.storage.save(LS_OPERACIONES, lista);
  }

  /** Registra un término buscado. Ignora vacíos y deduplica sin distinguir mayúsculas. */
  registrarConsulta(termino: string): void {
    const q = termino.trim();
    if (!q) return;

    const resto = this.consultasRaw().filter(
      (c) => c.toLowerCase() !== q.toLowerCase(),
    );
    const lista = [q, ...resto].slice(0, MAX_CONSULTAS);
    this.consultasRaw.set(lista);
    this.storage.save(LS_CONSULTAS, lista);
  }

  limpiar(): void {
    this.operacionesRaw.set([]);
    this.consultasRaw.set([]);
    this.storage.clear(LS_OPERACIONES);
    this.storage.clear(LS_CONSULTAS);
  }

  // ── Lectura saneada ───────────────────────────────────────────────────────

  /**
   * Todo lo que sale de localStorage se sanea: pueden venir tipos que ya no
   * existen o registros a medias.
   */
  private leerOperaciones(): OperacionReciente[] {
    const crudo = this.storage.getOrCreate<unknown[]>(LS_OPERACIONES, []);
    if (!Array.isArray(crudo)) return [];

    return crudo
      .filter((o): o is OperacionReciente => {
        const c = o as Partial<OperacionReciente>;
        return (
          !!c &&
          typeof c.id === 'number' &&
          typeof c.numero === 'string' &&
          typeof c.empresaId === 'number' &&
          typeof c.proveedorId === 'number' &&
          !!c.tipo &&
          TIPOS_RESULTADO.includes(c.tipo)
        );
      })
      .slice(0, MAX_OPERACIONES);
  }

  private leerConsultas(): string[] {
    const crudo = this.storage.getOrCreate<unknown[]>(LS_CONSULTAS, []);
    if (!Array.isArray(crudo)) return [];

    return crudo
      .filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
      .slice(0, MAX_CONSULTAS);
  }
}
